import React, { useState } from 'react';
import { X, Code, Users, Search, CheckCircle, AlertCircle } from 'lucide-react';

const JoinProjectModal = ({ show, onClose, onSubmit }) => {
    const [projectCode, setProjectCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleInputChange = (e) => {
        const value = e.target.value.toUpperCase();
        setProjectCode(value);

        // Clear messages when user starts typing
        if (error) setError('');
        if (success) setSuccess('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!projectCode.trim()) {
            setError('Please enter a project code');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const result = await onSubmit(projectCode.trim());
            if (result.success) {
                setSuccess(result.message || 'Successfully joined the project!');
                setTimeout(() => {
                    handleClose();
                }, 1500);
            } else {
                setError(result.message || 'Failed to join project');
            }
        } catch (error) {
            setError(error.message || 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setProjectCode('');
        setError('');
        setSuccess('');
        onClose();
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="modal-container small">
                <div className="modal-header">
                    <h2 className="modal-title">
                        <Code style={{ width: '20px', height: '20px' }} />
                        Join Project
                    </h2>
                    <button
                        className="modal-close"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <X style={{ width: '20px', height: '20px' }} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="join-project-info">
                        <div className="info-icon">
                            <Users style={{ width: '24px', height: '24px' }} />
                        </div>
                        <div className="info-text">
                            <h3>Join an Existing Project</h3>
                            <p>Enter the project code shared by your team member to join their project.</p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="join-form">
                        <div className="form-group">
                            <label htmlFor="projectCode" className="form-label">
                                <Code style={{ width: '16px', height: '16px' }} />
                                Project Code
                            </label>
                            <div className="code-input-container">
                                <input
                                    id="projectCode"
                                    type="text"
                                    value={projectCode}
                                    onChange={handleInputChange}
                                    className={`form-input code-input ${error ? 'error' : ''} ${success ? 'success' : ''}`}
                                    placeholder="PRJ-XXXXXX"
                                    disabled={isSubmitting}
                                    maxLength={10}
                                />
                                <div className="input-icon">
                                    <Search style={{ width: '16px', height: '16px' }} />
                                </div>
                            </div>

                            {error && (
                                <div className="message error">
                                    <AlertCircle style={{ width: '14px', height: '14px' }} />
                                    {error}
                                </div>
                            )}

                            {success && (
                                <div className="message success">
                                    <CheckCircle style={{ width: '14px', height: '14px' }} />
                                    {success}
                                </div>
                            )}
                        </div>

                        <div className="code-format-help">
                            <p className="help-text">
                                <strong>Format:</strong> Project codes are in the format PRJ-XXXXXX (e.g., PRJ-6ABXQZ)
                            </p>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="button secondary"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="button primary"
                                disabled={isSubmitting || !projectCode.trim()}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="spinner"></div>
                                        Joining...
                                    </>
                                ) : (
                                    <>
                                        <Users style={{ width: '16px', height: '16px' }} />
                                        Join Project
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default JoinProjectModal;